import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { GET_USER_LEVEL } from '../graphql/queries/userLevels';
import { GetUserLevelData } from '../types/userLevel';
import { LevelUpAnimation } from '../components/LevelUp/LevelUpAnimation';

interface LevelUpState {
  show: boolean;
  level: number;
  levelName: string;
  badge: string;
}

interface LevelUpContextType {
  checkForLevelUp: () => void;
  currentLevel: number | null;
  isLevelUpVisible: boolean;
}

const LevelUpContext = createContext<LevelUpContextType | undefined>(undefined);

export const useLevelUp = () => {
  const context = useContext(LevelUpContext);
  if (context === undefined) {
    throw new Error('useLevelUp must be used within a LevelUpProvider');
  }
  return context;
};

// Helper functions for localStorage management
const getStorageKey = (userId: string) => `levelUpShown_${userId}`;

const getLastShownLevel = (userId: string): number => {
  try {
    const stored = localStorage.getItem(getStorageKey(userId));
    return stored ? parseInt(stored, 10) : 0;
  } catch (error) {
    console.warn('Error reading from localStorage:', error);
    return 0;
  }
};

const setLastShownLevel = (userId: string, level: number): void => {
  try {
    localStorage.setItem(getStorageKey(userId), level.toString());
  } catch (error) {
    console.warn('Error writing to localStorage:', error);
  }
};

interface LevelUpProviderProps {
  children: React.ReactNode;
}

export const LevelUpProvider: React.FC<LevelUpProviderProps> = ({ children }) => {
  const [levelUpState, setLevelUpState] = useState<LevelUpState>({
    show: false,
    level: 0,
    levelName: '',
    badge: '',
  });

  // Query user level data
  const { data, refetch } = useQuery<GetUserLevelData>(GET_USER_LEVEL, {
    errorPolicy: 'all',
    pollInterval: 30000, // Poll every 30 seconds to check for updates
    notifyOnNetworkStatusChange: true,
  });

  const currentLevel = data?.getUserLevel?.currentLevel || null;
  const userId = data?.getUserLevel?.userId;

  // Check for level up when level data changes
  useEffect(() => {
    if (!data?.getUserLevel || !userId) return;

    const newLevel = data.getUserLevel.currentLevel;
    const lastShownLevel = getLastShownLevel(userId);
    
    // Check if user has leveled up since last shown animation
    if (newLevel > lastShownLevel) {
      setLevelUpState({
        show: true,
        level: newLevel,
        levelName: data.getUserLevel.currentLevelName,
        badge: data.getUserLevel.badge,
      });
      
      // Update localStorage with the new level
      setLastShownLevel(userId, newLevel);
    }
  }, [data, userId]);

  // Manual level up check (can be called after transactions)
  const checkForLevelUp = useCallback(async () => {
    if (!userId) return;
    
    try {
      const result = await refetch();
      const newLevel = result.data?.getUserLevel?.currentLevel;
      const lastShownLevel = getLastShownLevel(userId);
      
      if (newLevel && newLevel > lastShownLevel) {
        setLevelUpState({
          show: true,
          level: newLevel,
          levelName: result.data.getUserLevel.currentLevelName,
          badge: result.data.getUserLevel.badge,
        });
        
        // Update localStorage with the new level
        setLastShownLevel(userId, newLevel);
      }
    } catch (error) {
      console.error('Error checking for level up:', error);
    }
  }, [refetch, userId]);

  const handleAnimationComplete = useCallback(() => {
    setLevelUpState(prev => ({
      ...prev,
      show: false,
    }));
  }, []);

  const contextValue: LevelUpContextType = {
    checkForLevelUp,
    currentLevel,
    isLevelUpVisible: levelUpState.show,
  };

  return (
    <LevelUpContext.Provider value={contextValue}>
      {children}
      <LevelUpAnimation
        show={levelUpState.show}
        level={levelUpState.level}
        levelName={levelUpState.levelName}
        badge={levelUpState.badge}
        onComplete={handleAnimationComplete}
      />
    </LevelUpContext.Provider>
  );
}; 